/**
 * SettingsDialog – shared section-visibility toggle dialog.
 *
 * @param title         - dialog heading
 * @param description   - optional subtitle
 * @param open          - controlled open state
 * @param onOpenChange  - state setter
 * @param items         - ordered list of { key, label } toggle rows
 * @param values        - current boolean state keyed by item.key
 * @param onToggle      - called with key when a single row is toggled
 * @param onToggleAll   - called to flip every item at once
 */

import { Switch } from '@/components/ui/Switch';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';

export interface SettingsItem {
  key: string;
  label: string;
}

interface SettingsDialogProps {
  title: string;
  description?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: SettingsItem[];
  values: Record<string, boolean>;
  onToggle: (key: string) => void;
  onToggleAll: () => void;
}

export function SettingsDialog({
  title,
  description,
  open,
  onOpenChange,
  items,
  values,
  onToggle,
  onToggleAll,
}: SettingsDialogProps) {
  const allChecked = items.every((i) => values[i.key]);
  const noneChecked = items.every((i) => !values[i.key]);

  const statusLabel = allChecked
    ? 'All sections visible'
    : noneChecked
      ? 'All sections hidden'
      : 'Some sections hidden';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {/* Master visibility toggle */}
        <div
          className={cn(
            'flex items-center justify-between px-3 py-2.5 rounded-lg border mb-1',
            allChecked
              ? 'bg-primary/10 border-primary/30 text-primary'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
          )}
        >
          <Label htmlFor="settings-toggle-all" className="cursor-pointer text-sm font-medium">
            {statusLabel}
          </Label>
          <Switch
            id="settings-toggle-all"
            checked={allChecked}
            onCheckedChange={onToggleAll}
            className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-amber-500/60"
          />
        </div>

        {/* Per-section toggles */}
        <div
          className="space-y-1 max-h-[420px] overflow-y-auto pr-2"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'hsl(var(--muted-foreground) / 0.3) transparent',
          }}
        >
          {items.map(({ key, label }) => {
            const hidden = !values[key];
            return (
              <div
                key={key}
                className={cn(
                  'flex items-center justify-between px-3 py-2 rounded-md transition-colors',
                  hidden ? 'bg-zinc-900/60 opacity-50 hover:opacity-70' : 'hover:bg-zinc-800/40'
                )}
              >
                <Label
                  htmlFor={`settings-${key}`}
                  className={cn(
                    'cursor-pointer text-sm',
                    hidden && 'text-muted-foreground line-through'
                  )}
                >
                  {label}
                </Label>
                <Switch
                  id={`settings-${key}`}
                  checked={!!values[key]}
                  onCheckedChange={() => onToggle(key)}
                  className="data-[state=checked]:bg-emerald-500"
                />
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
